import React, { ReactElement, useState } from 'react';
import { Image, StyleSheet, Text, View } from 'react-native';
import BackSwipeIcon from '../../../assets/images/backSwipeIcon.svg';
import build from '../../../config/build';
import theme from '../../../theme';
import { defaultScale } from '../../../utils/scaling';

enum OnBoardingScreens {
  OnBoardingScreen1 = 'OnBoardingScreen1',
  OnBoardingScreen2 = 'OnBoardingScreen2',
  OnBoardingScreen3 = 'OnBoardingScreen3',
  OnBoardingScreen4 = 'OnBoardingScreen4',
}

interface IButton {
  onPress: () => void;
  text: string;
}

interface IOnBoardingConfig {
  heading: ReactElement;
  description?: ReactElement;
  image: ReactElement;
  buttons: IButton[];
}

interface IOutput {
  currentScreen: OnBoardingScreens;
  config: { [key: string]: IOnBoardingConfig };
}

const isELite = build.variant.isElite;

const OnBoardingDescription = ({ description }) => {
  return <Text style={styles.descriptionText}>{description}</Text>;
};

const OnBoardingHeading = ({ description }) => {
  return <Text style={styles.headingText}>{description}</Text>;
};

const OnBoardingScreen3Description = ({ t }) => {
  return isELite ? (
    <Text style={styles.descriptionText}>
      {t('open')}
      <Text style={styles.bold}> {t('spotifyUrl')} </Text>
      {t('signUpForSpotifyOnWeb')}
    </Text>
  ) : (
    <Text style={styles.descriptionText}>
      <Text style={styles.bold}>{t('install')} </Text>
      {t('fromAppOrPlaystore')}
      <Text style={styles.bold}> {t('logIn')}</Text> {t('or')}
      <Text style={styles.bold}> {t('signUp')} </Text>
      {t('forFreeOrPaidAccount')}
    </Text>
  );
};

const OnBoardingScreen4Description = ({ t }) => {
  return isELite ? (
    <OnBoardingDescription description={t('swipeBack')} />
  ) : (
    <View>
      <View style={styles.onBoardingLite4DescriptionContainer}>
        <Text style={[styles.descriptionText]}>{'\u2022'}</Text>
        <Text
          style={[styles.descriptionText, styles.onBoardingLite4Description]} >
          {t('stopSpotifyOnOtherDevices')}
        </Text>
      </View>
      <View style={styles.onBoardingLite4DescriptionContainer}>
        <Text style={styles.descriptionText}>{'\u2022'}</Text>
        <Text
          style={[styles.descriptionText, styles.onBoardingLite4Description]}
        >
          {t('restartSpotify')}
        </Text>
      </View>
    </View>
  );
};

const OnBoardingScreen4Image = () => {
  return (
    <>
      {isELite && (
        <View
          style={{
            position: 'absolute',
            top: '30%',
            left: defaultScale(70),
            zIndex: 10,
          }}
        >
          <BackSwipeIcon />
        </View>
      )}
      <Image
        style={styles.image}
        source={require('../../../assets/images/spotifyOnBoarding4.png')}
      />
    </>
  );
};

export const useSpotifyOnboardingConfig = (
  t: (key: string, options?: any) => string,
  startMusicOnSpotify: () => void,
): IOutput => {
  const [currentScreen, setCurrentScreen] = useState<OnBoardingScreens>(
    OnBoardingScreens.OnBoardingScreen1,
  );

  const config = {
    [OnBoardingScreens.OnBoardingScreen1]: {
      heading: (
        <OnBoardingHeading description={t('experienceCustomisedPlaylists')} />
      ),
      image: (
        <Image
          style={styles.image}
          source={require('../../../assets/images/spotifyOnBoarding1.png')}
        />
      ),
      description: (
        <OnBoardingDescription description={t('trainWithPlaylists')} />
      ),
      buttons: [
        {
          text: t('continue'),
          onPress: () => {
            setCurrentScreen(OnBoardingScreens.OnBoardingScreen2);
          },
        },
      ],
    },
    [OnBoardingScreens.OnBoardingScreen2]: {
      heading: (
        <OnBoardingHeading
          description={
            isELite ? t('doYouHaveSpotifyAccount') : t('isSpotifyOnYourDevice')
          }
        />
      ),
      image: (
        <Image
          style={styles.image}
          source={
            isELite
              ? require('../../../assets/images/spotifyOnBoardingElite2.png')
              : require('../../../assets/images/spotifyOnBoarding2.png')
          }
        />
      ),
      buttons: [
        {
          text: t('yes'),
          onPress: () => {
            setCurrentScreen(OnBoardingScreens.OnBoardingScreen4);
          },
        },
        {
          text: t('notYet'),
          onPress: () => {
            setCurrentScreen(OnBoardingScreens.OnBoardingScreen3);
          },
        },
      ],
    },
    [OnBoardingScreens.OnBoardingScreen3]: {
      heading: <OnBoardingHeading description={isELite ? t('signUpForSpotify') : t('setUpSpotify')} />,
      image: (
        <Image
          style={styles.image}
          source={require('../../../assets/images/spotifyOnBoarding3.png')}
        />
      ),
      description: <OnBoardingScreen3Description t={t} />,
      buttons: [
        {
          text: isELite ? t('spotifyAccountReady') : t('spotifyIsReady'),
          onPress: () => {
            setCurrentScreen(OnBoardingScreens.OnBoardingScreen4);
          },
        },
      ],
    },
    [OnBoardingScreens.OnBoardingScreen4]: {
      heading: (
        <OnBoardingHeading
          description={
            isELite ? t('toComeBackToFitillion') : t('getReadyToStartSpotify')
          }
        />
      ),
      image: <OnBoardingScreen4Image />,
      description: <OnBoardingScreen4Description t={t} />,
      buttons: [
        {
          text: t('startMusicOnSpotify'),
          onPress: () => {
            startMusicOnSpotify();
          },
        },
      ],
    },
  };
  return { config, currentScreen };
};

const styles = StyleSheet.create({
  headingText: {
    ...theme.textStyle.heading,
    marginBottom: defaultScale(15),
  },
  descriptionText: {
    ...theme.textStyle.bodyCopyMed,
    textAlign: 'center',
  },
  bold: {
    fontWeight: '700',
  },
  image: {
    width: '100%',
    height: '100%',
  },
  onBoardingLite4DescriptionContainer: {
    flexDirection: 'row',
    marginBottom: '3%',
  },
  onBoardingLite4Description: {
    paddingLeft: 5,
    textAlign: 'left',
  },
});
